import React from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  CircularProgress,
  StepIconProps,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowForwardIcon, CheckCircleIcon, ErrorIcon, RadioButtonCheckedIcon, RadioButtonUncheckedIcon } from "../../icons";
import {
  WorkflowDefinition,
  WorkflowTransition,
  RoadmapStep,
  RoadmapStepStatus,
} from "./workflowRoadmapTypes";

export interface WorkflowRoadmapProps {
  workflow: WorkflowDefinition;
  currentState: string;
  labels: Record<string, string>;
  transitions: WorkflowTransition[];
  onTransition: (transitionId: string) => void;
  isProcessing?: boolean;
  errorStates?: string[];
  forwardTransitions?: string[];
  /** Motivo operacional del último error (campo `*LastOperationalError` de la
   * entidad). Se muestra bajo el roadmap cuando el estado actual es de error,
   * para que el usuario vea la CAUSA accionable y no solo el label del estado. */
  operationalError?: string | null;
  /** Presentación para usuarios SIN capacidad de intervenir (p.ej. el cliente
   * del tenant, que no opera /automation): los estados de falla se muestran
   * como "en curso" (sin rojo, sin label de error) y el banner de
   * operationalError se suprime — los errores internos se resuelven del lado
   * del operador. Combinar con `notice` para las advertencias que SÍ son
   * accionables por el usuario. */
  concealErrors?: boolean;
  /** Advertencia NEUTRA accionable por el usuario (p.ej. "Falta comprobante
   * de pago DNDA"). Se muestra siempre que esté presente, también con
   * concealErrors activo. */
  notice?: string | null;
}

function calculateSteps(
  workflow: WorkflowDefinition,
  currentState: string,
  labels: Record<string, string>,
  concealErrors: boolean
): RoadmapStep[] {
  const currentIndex = workflow.mainPath.indexOf(currentState);
  const isInMainPath = currentIndex >= 0;
  const anchor = workflow.stateAnchors?.[currentState];
  const subLabelText = labels[currentState] || currentState;

  return workflow.mainPath.map((stateId, index) => {
    let status: RoadmapStepStatus;
    let subLabel: string | undefined;

    if (isInMainPath) {
      status = index < currentIndex ? "completed" : index === currentIndex ? "current" : "pending";
    } else if (anchor) {
      // Estado fuera del mainPath (transitorio o falla): las anclas precomputadas
      // por el generador dicen qué hitos quedaron atrás y cuál está en progreso.
      if (index <= anchor.afterIndex) {
        status = "completed";
      } else if (index === anchor.towardIndex) {
        const isError = anchor.kind === "error";
        // concealErrors: la falla se presenta como hito EN CURSO — sin rojo y
        // sin el label del estado de error ("Error Subiendo Ejemplar").
        status = isError && !concealErrors ? "error" : "current";
        subLabel = isError && concealErrors ? undefined : subLabelText;
      } else {
        status = "pending";
      }
    } else {
      // Estado inicial (fuera del mainPath) o sin ancla → todo pending.
      status = "pending";
    }

    return {
      id: stateId,
      label: labels[stateId] || stateId,
      status,
      subLabel,
    };
  });
}

function CustomStepIcon(props: StepIconProps & { stepStatus: RoadmapStepStatus }) {
  const { stepStatus } = props;

  const iconStyles = {
    fontSize: 24,
  };

  switch (stepStatus) {
    case "completed":
      return <CheckCircleIcon sx={{ ...iconStyles, color: "success.main" }} />;
    case "current":
      return <RadioButtonCheckedIcon sx={{ ...iconStyles, color: "primary.main" }} />;
    case "error":
      return <ErrorIcon sx={{ ...iconStyles, color: "error.main" }} />;
    case "pending":
    default:
      return <RadioButtonUncheckedIcon sx={{ ...iconStyles, color: "action.disabled" }} />;
  }
}

export const WorkflowRoadmap: React.FC<WorkflowRoadmapProps> = ({
  workflow,
  currentState,
  labels,
  transitions,
  onTransition,
  isProcessing = false,
  forwardTransitions = [],
  operationalError,
  concealErrors = false,
  notice,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const steps = calculateSteps(workflow, currentState, labels, concealErrors);
  const activeStepIndex = steps.findIndex((s) => s.status === "current" || s.status === "error");

  // Verificar si el estado actual está fuera del mainPath
  const isOutsideMainPath = !workflow.mainPath.includes(currentState);
  const isInitialState = currentState === workflow.initialState;
  const currentStateLabel = labels[currentState] || currentState;

  // La banda "Estado actual" solo aporta cuando hay algo que hacer (acciones o un
  // proceso en curso). Si el estado fuera del mainPath ya se muestra como subLabel
  // bajo su nodo (tiene ancla), sin acciones la banda es redundante. Sin ancla no
  // habría subLabel, así que la banda se mantiene para no dejar el estado invisible.
  const showStateBand =
    isOutsideMainPath &&
    !isInitialState &&
    (transitions.length > 0 || isProcessing || !workflow.stateAnchors?.[currentState]);

  // Separar transiciones primarias (forward) de secundarias
  const primaryTransitions = transitions.filter((t) =>
    forwardTransitions.includes(t.id)
  );
  const secondaryTransitions = transitions.filter(
    (t) => !forwardTransitions.includes(t.id)
  );

  return (
    <Box sx={{ width: "100%", pt: 1 }}>
      {/* Botones de transición cuando estamos en estado inicial */}
      {isInitialState && transitions.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "grey.100",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {currentStateLabel}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {isProcessing ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Procesando...
                </Typography>
              </Box>
            ) : (
              transitions.map((transition) => (
                <Button
                  key={transition.id}
                  variant="contained"
                  color="primary"
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => onTransition(transition.id)}
                >
                  {transition.label}
                </Button>
              ))
            )}
          </Box>
        </Box>
      )}

      <Stepper
        activeStep={activeStepIndex >= 0 ? activeStepIndex : -1}
        orientation={isMobile ? "vertical" : "horizontal"}
        alternativeLabel={!isMobile}
        sx={{
          "& .MuiStepConnector-line": {
            minHeight: isMobile ? 20 : undefined,
          },
        }}
      >
        {steps.map((step) => {
          const isCurrentStep = step.status === "current" || step.status === "error";

          return (
            <Step key={step.id} completed={step.status === "completed"}>
              <StepLabel
                StepIconComponent={(iconProps) => (
                  <CustomStepIcon {...iconProps} stepStatus={step.status} />
                )}
                sx={{
                  "& .MuiStepLabel-label": {
                    fontWeight: isCurrentStep ? 600 : 400,
                    color: step.status === "pending" ? "text.disabled" : "text.primary",
                  },
                }}
              >
                {step.label}
                {step.subLabel && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ color: step.status === "error" ? "error.main" : "primary.main", mt: 0.25 }}
                  >
                    {step.subLabel}
                  </Typography>
                )}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {/* Causa operacional del error: el label del estado ("Error en Registro
          DNDA") no dice QUÉ pasó ni qué hacer; el motivo vive en el campo
          *LastOperationalError de la entidad y acá se hace visible. Con
          concealErrors se SUPRIME: es material del operador, no del cliente. */}
      {!concealErrors &&
        operationalError &&
        (workflow.stateAnchors?.[currentState]?.kind === "error" || isOutsideMainPath) && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              bgcolor: "warning.light",
              borderRadius: 1,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <ErrorIcon fontSize="small" sx={{ color: "warning.dark", mt: 0.25 }} />
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {operationalError}
            </Typography>
          </Box>
        )}

      {/* Advertencia neutra accionable por el usuario (p.ej. falta el
          comprobante de pago): se muestra siempre, también con concealErrors. */}
      {notice && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: "warning.light",
            borderRadius: 1,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <ErrorIcon fontSize="small" sx={{ color: "warning.dark", mt: 0.25 }} />
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {notice}
          </Typography>
        </Box>
      )}

      {/* Banda "Estado actual" para estados fuera del mainPath: solo con acciones o proceso */}
      {showStateBand && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "info.light",
            borderRadius: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RadioButtonCheckedIcon sx={{ color: "info.dark" }} />
            <Typography variant="body2">
              Estado actual: <strong>{currentStateLabel}</strong>
            </Typography>
          </Box>
          {transitions.length > 0 && !isProcessing && (
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column-reverse" : "row",
                gap: 1,
                ml: isMobile ? 0 : "auto",
                mt: isMobile ? 1 : 0,
              }}
            >
              {secondaryTransitions.map((transition) => (
                <Button
                  key={transition.id}
                  variant="outlined"
                  color="inherit"
                  size="small"
                  fullWidth={isMobile}
                  onClick={() => onTransition(transition.id)}
                >
                  {transition.label}
                </Button>
              ))}
              {primaryTransitions.map((transition) => (
                <Button
                  key={transition.id}
                  variant="contained"
                  color="primary"
                  size={isMobile ? "medium" : "small"}
                  fullWidth={isMobile}
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => onTransition(transition.id)}
                >
                  {transition.label}
                </Button>
              ))}
            </Box>
          )}
          {isProcessing && <CircularProgress size={20} sx={{ ml: isMobile ? 0 : "auto", alignSelf: isMobile ? "center" : undefined }} />}
        </Box>
      )}

      {/* Botones de acción debajo del stepper para estados en mainPath */}
      {!isInitialState && !isOutsideMainPath && transitions.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column-reverse" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: 1,
            mt: 3,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          {isProcessing ? (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%", gap: 1 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Procesando...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Botones secundarios (izquierda en desktop, abajo en mobile) */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {secondaryTransitions.map((transition) => (
                  <Button
                    key={transition.id}
                    variant="outlined"
                    color="inherit"
                    size="small"
                    fullWidth={isMobile}
                    onClick={() => onTransition(transition.id)}
                  >
                    {transition.label}
                  </Button>
                ))}
              </Box>

              {/* Botones primarios (derecha en desktop, arriba en mobile) */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {primaryTransitions.map((transition) => (
                  <Button
                    key={transition.id}
                    variant="contained"
                    color="primary"
                    size={isMobile ? "medium" : "small"}
                    fullWidth={isMobile}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => onTransition(transition.id)}
                  >
                    {transition.label}
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
